import * as d3 from 'd3'

interface DataPoint {
  date: string
  count: number
}

interface Series {
  name: string
  color: string
  primary?: boolean
  data: DataPoint[]
}

interface ParsedDataPoint {
  date: Date
  count: number
}

interface ParsedSeries {
  name: string
  color: string
  primary: boolean
  data: ParsedDataPoint[]
}

class SalesChartElement extends HTMLElement {
  private data: DataPoint[] = []
  private series: Series[] = []
  private resizeObserver: ResizeObserver | null = null
  private gradientId = `areaGradient-${Math.random().toString(36).slice(2, 9)}`
  private tooltipEl: HTMLDivElement | null = null

  connectedCallback() {
    const seriesAttr = this.getAttribute('data-chart-series')
    const dataAttr = this.getAttribute('data-chart-data')

    if (seriesAttr) {
      this.series = JSON.parse(seriesAttr)
    } else if (dataAttr) {
      this.data = JSON.parse(dataAttr)
    }

    this.render()
    this.resizeObserver = new ResizeObserver(() => this.render())
    this.resizeObserver.observe(this)
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect()
    this.removeTooltip()
  }

  private removeTooltip() {
    if (this.tooltipEl) {
      this.tooltipEl.remove()
      this.tooltipEl = null
    }
  }

  private createTooltip(): d3.Selection<HTMLDivElement, unknown, null, undefined> {
    this.removeTooltip()
    this.tooltipEl = document.createElement('div')
    this.tooltipEl.className = 'chart-tooltip'
    Object.assign(this.tooltipEl.style, {
      position: 'fixed',
      background: 'rgb(24, 24, 27)',
      border: '1px solid rgb(63, 63, 70)',
      borderRadius: '8px',
      padding: '8px 12px',
      fontSize: '12px',
      color: 'rgb(244, 244, 245)',
      pointerEvents: 'none',
      opacity: '0',
      transition: 'opacity 0.15s ease',
      zIndex: '9999',
      whiteSpace: 'nowrap',
    })
    document.body.appendChild(this.tooltipEl)
    return d3.select(this.tooltipEl)
  }

  private render() {
    this.innerHTML = ''
    this.removeTooltip()

    if (this.series.length > 0) {
      this.renderMultiSeries()
    } else if (this.data.length > 0) {
      this.renderSingleSeries()
    }
  }

  private renderSingleSeries() {
    if (this.data.length === 0) return

    const containerWidth = this.clientWidth || 640
    const width = containerWidth
    const height = 220
    const margin = { top: 20, right: 20, bottom: 40, left: 50 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const svg = d3.select(this)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')

    // Gradient definition
    const defs = svg.append('defs')

    const gradient = defs.append('linearGradient')
      .attr('id', this.gradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%')

    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', 'rgb(59, 130, 246)')
      .attr('stop-opacity', 0.25)

    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', 'rgb(59, 130, 246)')
      .attr('stop-opacity', 0)

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Parse dates and create scales
    const parseDate = d3.timeParse('%Y-%m-%d')
    const parsedData = this.data.map(d => ({
      date: parseDate(d.date) as Date,
      count: d.count
    }))

    // Handle single data point case - create a range around the single date
    let xDomain: [Date, Date]
    if (parsedData.length === 1) {
      const singleDate = parsedData[0].date
      const dayBefore = new Date(singleDate.getTime() - 24 * 60 * 60 * 1000)
      const dayAfter = new Date(singleDate.getTime() + 24 * 60 * 60 * 1000)
      xDomain = [dayBefore, dayAfter]
    } else {
      xDomain = d3.extent(parsedData, d => d.date) as [Date, Date]
    }

    const xScale = d3.scaleTime()
      .domain(xDomain)
      .range([0, innerWidth])

    const maxCount = d3.max(parsedData, d => d.count) || 1
    const tickInterval = this.getNiceTickInterval(maxCount)
    const yMax = Math.ceil(maxCount / tickInterval) * tickInterval || tickInterval
    const yScale = d3.scaleLinear()
      .domain([0, yMax])
      .range([innerHeight, 0])

    // Grid lines with nice round numbers
    const yAxisTicks = d3.range(0, yMax + tickInterval, tickInterval).filter(t => t <= yMax)
    g.selectAll('.grid-line')
      .data(yAxisTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('y1', d => yScale(d))
      .attr('x2', innerWidth)
      .attr('y2', d => yScale(d))
      .attr('stroke', 'rgb(63, 63, 70)')
      .attr('stroke-opacity', 0.2)

    // Smooth curve generator
    const line = d3.line<{ date: Date; count: number }>()
      .x(d => xScale(d.date))
      .y(d => yScale(d.count))
      .curve(d3.curveCatmullRom.alpha(0.5))

    // Area generator
    const area = d3.area<{ date: Date; count: number }>()
      .x(d => xScale(d.date))
      .y0(innerHeight)
      .y1(d => yScale(d.count))
      .curve(d3.curveCatmullRom.alpha(0.5))

    // Draw area (only if we have more than one point)
    if (parsedData.length > 1) {
      g.append('path')
        .datum(parsedData)
        .attr('fill', `url(#${this.gradientId})`)
        .attr('d', area)

      // Draw line with animation
      const path = g.append('path')
        .datum(parsedData)
        .attr('fill', 'none')
        .attr('stroke', 'rgb(59, 130, 246)')
        .attr('stroke-width', 2.5)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('d', line)

      // Line animation
      const pathLength = (path.node() as SVGPathElement).getTotalLength()
      path
        .attr('stroke-dasharray', pathLength)
        .attr('stroke-dashoffset', pathLength)
        .transition()
        .duration(1000)
        .ease(d3.easeQuadOut)
        .attr('stroke-dashoffset', 0)
    }

    // Draw data points for all data (especially important for single point)
    g.selectAll('.data-point')
      .data(parsedData)
      .enter()
      .append('circle')
      .attr('class', 'data-point')
      .attr('cx', d => xScale(d.date))
      .attr('cy', d => yScale(d.count))
      .attr('r', parsedData.length === 1 ? 6 : 4)
      .attr('fill', 'rgb(59, 130, 246)')
      .attr('stroke', 'rgb(24, 24, 27)')
      .attr('stroke-width', 2)
      .style('opacity', parsedData.length === 1 ? 1 : 0)

    // Y-axis labels
    g.selectAll('.y-label')
      .data(yAxisTicks)
      .enter()
      .append('text')
      .attr('class', 'y-label')
      .attr('x', -12)
      .attr('y', d => yScale(d))
      .attr('dy', '0.32em')
      .attr('text-anchor', 'end')
      .attr('fill', 'rgb(113, 113, 122)')
      .attr('font-size', '11px')
      .attr('font-family', 'ui-monospace, SFMono-Regular, SF Mono, Menlo, monospace')
      .text(d => d)

    // X-axis labels - show select dates
    const xTicks = this.getXAxisTicks(parsedData)
    const formatDate = d3.timeFormat('%b %-d')

    g.selectAll('.x-label')
      .data(xTicks)
      .enter()
      .append('text')
      .attr('class', 'x-label')
      .attr('x', d => xScale(d.date))
      .attr('y', innerHeight + 24)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgb(113, 113, 122)')
      .attr('font-size', '11px')
      .attr('font-family', 'ui-sans-serif, system-ui, sans-serif')
      .text(d => formatDate(d.date))

    // Interactive dots with tooltip
    const tooltip = this.createTooltip()
    const chartEl = this

    // Invisible overlay for mouse tracking
    const bisect = d3.bisector<{ date: Date; count: number }, Date>(d => d.date).left

    const focusDot = g.append('circle')
      .attr('r', 5)
      .attr('fill', 'rgb(59, 130, 246)')
      .attr('stroke', 'rgb(24, 24, 27)')
      .attr('stroke-width', 2)
      .style('opacity', 0)

    const focusLine = g.append('line')
      .attr('stroke', 'rgb(59, 130, 246)')
      .attr('stroke-opacity', 0.3)
      .attr('stroke-dasharray', '4 4')
      .style('opacity', 0)

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = d3.pointer(event)
        const x0 = xScale.invert(mx)
        const i = bisect(parsedData, x0, 1)
        const d0 = parsedData[i - 1]
        const d1 = parsedData[i]
        if (!d0 || !d1) return
        const d = x0.getTime() - d0.date.getTime() > d1.date.getTime() - x0.getTime() ? d1 : d0

        const cx = xScale(d.date)
        const cy = yScale(d.count)

        focusDot
          .attr('cx', cx)
          .attr('cy', cy)
          .style('opacity', 1)

        focusLine
          .attr('x1', cx)
          .attr('y1', cy)
          .attr('x2', cx)
          .attr('y2', innerHeight)
          .style('opacity', 1)

        const formatTooltipDate = d3.timeFormat('%B %-d, %Y')
        const saleWord = d.count === 1 ? 'sale' : 'sales'
        const rect = chartEl.getBoundingClientRect()
        const svgEl = chartEl.querySelector('svg')
        const scaleX = svgEl ? rect.width / (svgEl.viewBox.baseVal.width || rect.width) : 1
        const scaleY = svgEl ? rect.height / (svgEl.viewBox.baseVal.height || rect.height) : 1
        tooltip
          .html(`<div style="font-weight: 500; margin-bottom: 2px;">${d.count} ${saleWord}</div><div style="color: rgb(161, 161, 170); font-size: 11px;">${formatTooltipDate(d.date)}</div>`)
          .style('left', `${rect.left + (margin.left + cx) * scaleX}px`)
          .style('top', `${rect.top + (margin.top + cy) * scaleY - 50}px`)
          .style('transform', 'translateX(-50%)')
          .style('opacity', 1)
      })
      .on('mouseleave', () => {
        focusDot.style('opacity', 0)
        focusLine.style('opacity', 0)
        tooltip.style('opacity', 0)
      })
  }

  private renderMultiSeries() {
    const parseDate = d3.timeParse('%Y-%m-%d')

    const allSeries: ParsedSeries[] = this.series
      .map(s => ({
        name: s.name,
        color: s.color,
        primary: s.primary ?? false,
        data: s.data.map(d => ({
          date: parseDate(d.date) as Date,
          count: d.count,
        })),
      }))
      .filter(s => s.data.length > 0)

    if (allSeries.length === 0) return

    const containerWidth = this.clientWidth || 640
    const width = containerWidth
    const height = 220
    const margin = { top: 20, right: 20, bottom: 40, left: 50 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const svg = d3.select(this)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')

    // Gradient for primary series area fill
    const defs = svg.append('defs')
    const primarySeries = allSeries.find(s => s.primary)
    if (primarySeries) {
      const gradient = defs.append('linearGradient')
        .attr('id', this.gradientId)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%')

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', primarySeries.color)
        .attr('stop-opacity', 0.15)

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', primarySeries.color)
        .attr('stop-opacity', 0)
    }

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Compute shared scales across all series
    const allDates = allSeries.flatMap(s => s.data.map(d => d.date))
    const xDomain = d3.extent(allDates) as [Date, Date]
    const xScale = d3.scaleTime().domain(xDomain).range([0, innerWidth])

    const maxCount = d3.max(allSeries, s => d3.max(s.data, d => d.count)) || 1
    const tickInterval = this.getNiceTickInterval(maxCount)
    const yMax = Math.ceil(maxCount / tickInterval) * tickInterval || tickInterval
    const yScale = d3.scaleLinear().domain([0, yMax]).range([innerHeight, 0])

    // Grid lines
    const yAxisTicks = d3.range(0, yMax + tickInterval, tickInterval).filter(t => t <= yMax)
    g.selectAll('.grid-line')
      .data(yAxisTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('y1', d => yScale(d))
      .attr('x2', innerWidth)
      .attr('y2', d => yScale(d))
      .attr('stroke', 'rgb(63, 63, 70)')
      .attr('stroke-opacity', 0.2)

    // Curve generators
    const line = d3.line<ParsedDataPoint>()
      .x(d => xScale(d.date))
      .y(d => yScale(d.count))
      .curve(d3.curveCatmullRom.alpha(0.5))

    const area = d3.area<ParsedDataPoint>()
      .x(d => xScale(d.date))
      .y0(innerHeight)
      .y1(d => yScale(d.count))
      .curve(d3.curveCatmullRom.alpha(0.5))

    // Draw primary series first (area fill + thick line)
    if (primarySeries && primarySeries.data.length > 1) {
      g.append('path')
        .datum(primarySeries.data)
        .attr('fill', `url(#${this.gradientId})`)
        .attr('d', area)

      const path = g.append('path')
        .datum(primarySeries.data)
        .attr('fill', 'none')
        .attr('stroke', primarySeries.color)
        .attr('stroke-width', 2.5)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('d', line)

      const pathLength = (path.node() as SVGPathElement).getTotalLength()
      path
        .attr('stroke-dasharray', pathLength)
        .attr('stroke-dashoffset', pathLength)
        .transition()
        .duration(1000)
        .ease(d3.easeQuadOut)
        .attr('stroke-dashoffset', 0)
    }

    // Draw secondary series lines on top
    allSeries.filter(s => !s.primary).forEach(series => {
      if (series.data.length <= 1) return

      const path = g.append('path')
        .datum(series.data)
        .attr('fill', 'none')
        .attr('stroke', series.color)
        .attr('stroke-width', 1.5)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('d', line)

      const pathLength = (path.node() as SVGPathElement).getTotalLength()
      path
        .attr('stroke-dasharray', pathLength)
        .attr('stroke-dashoffset', pathLength)
        .transition()
        .duration(1000)
        .ease(d3.easeQuadOut)
        .attr('stroke-dashoffset', 0)
    })

    // Y-axis labels
    g.selectAll('.y-label')
      .data(yAxisTicks)
      .enter()
      .append('text')
      .attr('class', 'y-label')
      .attr('x', -12)
      .attr('y', d => yScale(d))
      .attr('dy', '0.32em')
      .attr('text-anchor', 'end')
      .attr('fill', 'rgb(113, 113, 122)')
      .attr('font-size', '11px')
      .attr('font-family', 'ui-monospace, SFMono-Regular, SF Mono, Menlo, monospace')
      .text(d => d)

    // X-axis labels
    const refData = allSeries[0].data
    const xTicks = this.getXAxisTicks(refData)
    const formatDate = d3.timeFormat('%b %-d')

    g.selectAll('.x-label')
      .data(xTicks)
      .enter()
      .append('text')
      .attr('class', 'x-label')
      .attr('x', d => xScale(d.date))
      .attr('y', innerHeight + 24)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgb(113, 113, 122)')
      .attr('font-size', '11px')
      .attr('font-family', 'ui-sans-serif, system-ui, sans-serif')
      .text(d => formatDate(d.date))

    // Tooltip
    const tooltip = this.createTooltip()
    const chartEl = this

    // Focus dots for each series
    const focusDots = allSeries.map(series =>
      g.append('circle')
        .attr('r', 4)
        .attr('fill', series.color)
        .attr('stroke', 'rgb(24, 24, 27)')
        .attr('stroke-width', 2)
        .style('opacity', 0)
    )

    const focusLine = g.append('line')
      .attr('stroke', 'rgb(113, 113, 122)')
      .attr('stroke-opacity', 0.3)
      .attr('stroke-dasharray', '4 4')
      .style('opacity', 0)

    // Use first series for date bisecting (all share same date range)
    const bisect = d3.bisector<ParsedDataPoint, Date>(d => d.date).left

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = d3.pointer(event)
        const x0 = xScale.invert(mx)
        const i = bisect(refData, x0, 1)
        const d0 = refData[i - 1]
        const d1 = refData[i]
        if (!d0 && !d1) return

        let dateIdx: number
        if (!d0) dateIdx = i
        else if (!d1) dateIdx = i - 1
        else dateIdx = x0.getTime() - d0.date.getTime() > d1.date.getTime() - x0.getTime() ? i : i - 1

        const hoveredDate = refData[dateIdx]?.date
        if (!hoveredDate) return

        const cx = xScale(hoveredDate)

        // Update focus dots
        allSeries.forEach((series, si) => {
          const point = series.data[dateIdx]
          if (point) {
            focusDots[si]
              .attr('cx', cx)
              .attr('cy', yScale(point.count))
              .style('opacity', 1)
          }
        })

        // Update focus line
        focusLine
          .attr('x1', cx)
          .attr('y1', 0)
          .attr('x2', cx)
          .attr('y2', innerHeight)
          .style('opacity', 1)

        // Build tooltip
        const formatTooltipDate = d3.timeFormat('%B %-d, %Y')
        let tooltipHtml = `<div style="color: rgb(161, 161, 170); font-size: 11px; margin-bottom: 4px;">${formatTooltipDate(hoveredDate)}</div>`

        allSeries.forEach((series, si) => {
          const point = series.data[dateIdx]
          if (point) {
            tooltipHtml += `<div style="display: flex; align-items: center; gap: 6px; margin-top: 2px;"><span style="width: 8px; height: 8px; border-radius: 50%; background: ${series.color}; display: inline-block; flex-shrink: 0;"></span><span style="flex: 1;">${series.name}</span><span style="font-weight: 500; margin-left: 8px;">${point.count}</span></div>`
          }
        })

        const rect = chartEl.getBoundingClientRect()
        const svgEl = chartEl.querySelector('svg')
        const scaleX = svgEl ? rect.width / (svgEl.viewBox.baseVal.width || rect.width) : 1
        const scaleY = svgEl ? rect.height / (svgEl.viewBox.baseVal.height || rect.height) : 1
        tooltip
          .html(tooltipHtml)
          .style('left', `${rect.left + (margin.left + cx) * scaleX}px`)
          .style('top', `${rect.top + margin.top * scaleY - 10}px`)
          .style('transform', 'translate(-50%, -100%)')
          .style('opacity', 1)
      })
      .on('mouseleave', () => {
        focusDots.forEach(dot => dot.style('opacity', 0))
        focusLine.style('opacity', 0)
        tooltip.style('opacity', 0)
      })
  }

  private getNiceTickInterval(max: number): number {
    if (max <= 5) return 1
    if (max <= 10) return 2
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)))
    const normalized = max / magnitude
    if (normalized <= 2) return magnitude / 2
    if (normalized <= 5) return magnitude
    return magnitude * 2
  }

  private getXAxisTicks(data: { date: Date; count: number }[]): { date: Date; count: number }[] {
    if (data.length <= 1) return data
    if (data.length <= 5) return data

    // Show first, middle, and last
    const first = data[0]
    const middle = data[Math.floor(data.length / 2)]
    const last = data[data.length - 1]
    return [first, middle, last]
  }
}

customElements.define('sales-chart', SalesChartElement)
